import { useState, useEffect, useRef, useMemo } from "react";
import logger from "../../../lib/logger";

const useMediaQuery = (query) => {
  const initialMatches = useMemo(() => {
    try {
      return window.matchMedia?.(query)?.matches ?? false;
    } catch {
      return false;
    }
  }, [query]);

  const [matches, setMatches] = useState(initialMatches);
  const mediaQueryRef = useRef(null);

  useEffect(() => {
    if (!window.matchMedia) {
      setMatches(true);

      return;
    }

    try {
      mediaQueryRef.current = window.matchMedia(query);

      if (!mediaQueryRef.current) {
        setMatches(false);

        return;
      }

      if (mediaQueryRef.current.matches !== matches) {
        setMatches(mediaQueryRef.current.matches);
      }

      const handleMediaChange = (e) => {
        setMatches(e.matches);
      };

      mediaQueryRef.current.addEventListener("change", handleMediaChange, {
        passive: true,
      });

      return () => {
        if (mediaQueryRef.current) {
          mediaQueryRef.current.removeEventListener(
            "change",
            handleMediaChange,
          );
        }
      };
    } catch (error) {
      const errorText = "Failed to setup media query";
      logger.warn(new Error(`${errorText}: ${error.message}`));
      setMatches(true);
    }
  }, [query]);

  return matches;
};

export default useMediaQuery;
